import React, { useCallback } from 'react'
import { StyleSheet, TouchableOpacity } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { observer } from 'mobx-react-lite'
import { useFocusEffect, useRouter } from 'expo-router'

import { DS } from '../../design-system'
import { useStore } from '../../models'
import { formatDate } from '@ppwr/poker'
import { capitalizeFirstLetter } from '../../utils/formatString'
import ProfileIcon from '../../../assets/tabNavigator/Profile.svg'
import { useSubscriptionGuard } from '../../hooks/useSubscriptionGuard'

const MembershipBillingDetails: React.FC = observer(() => {
  const router = useRouter()
  const {
    session: { user },
    memberships: { subscription, getBillingDetails, billingDetails }
  } = useStore()

  useSubscriptionGuard()
  useFocusEffect(
    useCallback(() => {
      getBillingDetails()
    }, [getBillingDetails])
  )

  const onPressBack = () => {
    if (router.canGoBack()) {
      router.back()
      return
    }
    router.navigate('/(root)/(tabs)/membership')
  }

  return (
    <DS.Frame style={styles.wrapperContainer}>
      <DS.Frame style={styles.headerDivider} />
      <SafeAreaView style={styles.safeAreaContainer}>
        <DS.Frame style={styles.container}>
          <DS.Frame style={styles.headerContainer}>
            <DS.Text variant="Body Medium/Medium">Billing Details</DS.Text>
            <TouchableOpacity style={styles.returnButton} onPress={onPressBack}>
              <DS.Icon name="Arrow---left" size={20} color="Global/Black" />
            </TouchableOpacity>
          </DS.Frame>

          <DS.Frame style={styles.card}>
            <DS.Frame style={styles.cardItem}>
              <ProfileIcon />
              <DS.Frame style={styles.fullNameText}>
                <DS.Text variant="Body Medium/Medium">{user?.name}</DS.Text>
              </DS.Frame>
            </DS.Frame>
            <DS.Frame style={styles.divider} />

            <DS.Frame style={styles.cardItem}>
              <DS.Frame style={styles.cardItemLeft}>
                <DS.Text variant="Body Medium/Medium">{user?.email}</DS.Text>
              </DS.Frame>
            </DS.Frame>

            <DS.Frame style={styles.divider} />

            <DS.Frame style={styles.cardItem}>
              <DS.Frame style={styles.cardItemLeft}>
                <DS.Text variant="Body Medium/Medium">{`**** ****  **** ${billingDetails?.last4 ?? ''}`}</DS.Text>
              </DS.Frame>
            </DS.Frame>
          </DS.Frame>

          <DS.Frame style={styles.periodContainer}>
            <DS.Text variant="Body Medium/Medium">Current Plan</DS.Text>
            <DS.Text variant="Body Medium/Regular">{`Premium ${capitalizeFirstLetter(
              subscription?.type ?? ''
            )} • Recurring`}</DS.Text>
            <DS.Text variant="Body Medium/Regular">{`${formatDate(subscription?.startOfPeriod ?? '')} - ${formatDate(
              subscription?.endOfPeriod ?? ''
            )}`}</DS.Text>
          </DS.Frame>
        </DS.Frame>
      </SafeAreaView>
    </DS.Frame>
  )
})

const styles = StyleSheet.create({
  wrapperContainer: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  container: {
    flex: 1,
    padding: 24,
    paddingBottom: 16,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    backgroundColor: DS.palette['Brand/Cream'],
    position: 'relative',
    borderTopRightRadius: 24,
    borderTopLeftRadius: 24
  },
  headerContainer: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  },
  card: {
    width: '100%',
    borderWidth: 1,
    borderColor: DS.palette['Brand/Cream'],
    borderRadius: 16,
    backgroundColor: DS.palette['Global/White'],
    marginTop: 24,
    padding: 16,
    justifyContent: 'space-between',
    gap: 16
  },
  cardItem: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 8
  },
  cardItemLeft: {
    marginLeft: 36
  },
  returnButton: {
    padding: 8,
    borderRadius: 16,
    position: 'absolute',
    width: 40,
    height: 40,
    top: -8,
    left: 0
  },
  stripeButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: DS.palette['Global/White'],
    padding: 16,
    width: '100%',
    borderRadius: 16,
    marginTop: 24
  },
  divider: {
    height: 1,
    backgroundColor: DS.palette['Brand/Cream'],
    width: '100%',
    marginLeft: 36
  },
  periodContainer: {
    marginVertical: 24,
    gap: 2
  },
  fullNameText: {
    marginLeft: 2
  },
  headerDivider: {
    height: 90,
    backgroundColor: DS.palette['Global/Black'],
    width: '100%'
  },
  safeAreaContainer: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream'],
    borderTopRightRadius: 24,
    borderTopLeftRadius: 24,
    transform: [{ translateY: -24 }]
  }
})

export default MembershipBillingDetails
